import {
  TimelineStart,
  StepIndicator,
  TimelineIndicator,
  TimelineEnd,
} from "~/molecules/WorkflowTimelineIndicators";
import { useTranslation } from "react-i18next";
import {
  WorkflowStepStatuses,
  type DocumentDetailsVersion,
  type DocumentVersionDetails,
} from "~/api/types";
import { Link } from "react-router";
import { Button } from "~/atoms/button";
import { Archive, BookLock, CircleX } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "react-toastify";
import { moveWorkflow } from "~/api/v2/requests";
import { formatDate } from "~/helpers/date";
import { SetAdoptionDateModal } from "~/molecules/SetAdoptionDateModal";
import { PublishModal } from "~/molecules/PublishModal";
import { WorkflowOverviewStepControlContainer } from "~/molecules/WorkflowOverviewStepControlContainer";
import { WorkflowOverviewStep } from "~/molecules/WorkflowOverviewStep";
import WorkflowDescription from "~/molecules/WorkflowDescription";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "~/atoms/alert-dialog";
import {
  canEditWorkflow,
  canManageVersion,
  canPublishVersion,
  canSetWorkflowAdoptionDate,
  getWorkflowStepControlPermissions,
  isDraftVersion,
} from "~/helpers/documentPermissions";
import NoResult from "~/molecules/NoResult";
import WorkflowDates from "~/molecules/WorkflowDates";

function CancelWorkflowDialog({
  onConfirm,
  children,
}: {
  onConfirm: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(($) => $.commons.workflow.cancelWorkflowTitle)}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(($) => $.commons.workflow.cancelWorkflowDescription)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {t(($) => $.commons.workflow.abort)}
          </AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={onConfirm}>
            {t(($) => $.commons.workflow.cancelWorkflow)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface Props {
  documentVersion?: DocumentVersionDetails;
  selectedVersion: DocumentDetailsVersion;
  onPublish: (versionId: number) => void;
  onChange: () => void;
}

export const WorkflowOverview = ({
  documentVersion: documentVersion,
  selectedVersion,
  onPublish,
  onChange,
}: Props) => {
  const { t } = useTranslation();

  const isDraft = isDraftVersion(selectedVersion);

  const [isWorkflowExpanded, setIsWorkflowExpanded] = useState(isDraft);
  const [prevIsDraft, setPrevIsDraft] = useState(isDraft);
  if (prevIsDraft !== isDraft) {
    setPrevIsDraft(isDraft);
    setIsWorkflowExpanded(isDraft);
  }

  const [isCancelling, setIsCancelling] = useState(false);

  const canEditCurrentWorkflow = canEditWorkflow(selectedVersion);
  const canManage = canManageVersion(selectedVersion);

  if (documentVersion === undefined) {
    return <NoResult />;
  }

  const handleCancelWorkflow = async () => {
    try {
      setIsCancelling(true);
      await moveWorkflow(documentVersion.id, "cancel");
      toast.success(t(($) => $.toasts.success.workflowCancelled));
      void onChange();
    } catch {
      toast.error(t(($) => $.toasts.error.workflowNotCancelled));
    } finally {
      setIsCancelling(false);
    }
  };

  const lastStep = documentVersion.steps[documentVersion.steps.length - 1];
  const lastStepIndex = documentVersion.steps.length - 1;
  const isLastStepActive = lastStep?.status === WorkflowStepStatuses.active;

  const showSetAdoptionDate = canSetWorkflowAdoptionDate(
    selectedVersion,
    isLastStepActive,
  );

  const { showPublishButton } = getWorkflowStepControlPermissions({
    canManage,
    canPublish: canPublishVersion(selectedVersion),
    isWorkflowStarted: documentVersion.isOngoing,
    isActiveStep: isLastStepActive,
    isFirstStep: lastStepIndex === 0,
    isLastStep: true,
    versionStatus: selectedVersion.status,
  });

  const hasNoActivity =
    documentVersion.stepInstances.length === 1 &&
    (documentVersion.stepInstances[0]?.comments.length ?? 0) === 0;
  const showCancelButton =
    canManage && isDraft && documentVersion.isOngoing && hasNoActivity;

  return (
    <div className="flex flex-col gap-4">
      {canEditCurrentWorkflow && (
        <Button variant="outline" asChild>
          <Link to={`/workflows/${documentVersion.id}/edit`}>
            {t(($) => $.commons.workflow.editWorkflow)}
          </Link>
        </Button>
      )}
      <WorkflowDates documentVersion={documentVersion} />
      <WorkflowDescription description={documentVersion.description} />
      <div className="relative grid grid-cols-[3rem_1fr] gap-x-2 gap-y-4">
        <div className="bg-primary/40 absolute top-0 bottom-0 left-6 z-0 w-0.5" />
        <TimelineStart startedAt={documentVersion.stepInstances[0]?.createdAt}>
          {showCancelButton && (
            <CancelWorkflowDialog onConfirm={() => void handleCancelWorkflow()}>
              <Button variant="outline" disabled={isCancelling} size="sm">
                <CircleX />
                {t(($) => $.commons.workflow.cancelWorkflow)}
              </Button>
            </CancelWorkflowDialog>
          )}
        </TimelineStart>
        {isWorkflowExpanded ? (
          documentVersion.steps.map((step, index) => (
            <WorkflowOverviewStepControlContainer
              key={step.id}
              step={step}
              stepIndex={index}
              totalSteps={documentVersion.steps.length}
              documentVersionId={documentVersion.id}
              selectedVersion={selectedVersion}
              isWorkflowStarted={documentVersion.isOngoing}
              onChange={onChange}
            >
              <StepIndicator status={step.status} />
              <div className="min-w-0">
                <WorkflowOverviewStep
                  step={step}
                  stepInstances={documentVersion.stepInstances}
                  selectedVersionStatus={selectedVersion.status}
                  onUserResponse={onChange}
                />
              </div>
            </WorkflowOverviewStepControlContainer>
          ))
        ) : (
          <>
            <StepIndicator value="..." status={WorkflowStepStatuses.upcoming} />
            <div className="flex items-center text-sm italic">
              {documentVersion.steps.length}{" "}
              {t(($) => $.commons.workflow.completedSteps)}
            </div>
          </>
        )}

        {!isDraft && (
          <div className="col-start-2 flex justify-center">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setIsWorkflowExpanded(!isWorkflowExpanded)}
            >
              {isWorkflowExpanded
                ? t(($) => $.commons.workflow.hideSteps)
                : t(($) => $.commons.workflow.showSteps)}
            </Button>
          </div>
        )}

        {showSetAdoptionDate && (
          <TimelineIndicator
            icon={BookLock}
            text={`${t(($) => $.commons.document.adopted)} ${formatDate(selectedVersion.adoptedAt, "date")}`}
            variant={selectedVersion.adoptedAt ? "primary" : "secondary"}
          />
        )}
        {showSetAdoptionDate && (
          <div className="col-start-2 flex justify-center">
            <SetAdoptionDateModal
              adoptedAt={selectedVersion.adoptedAt}
              documentVersionId={selectedVersion.id}
              onChange={onChange}
            />
          </div>
        )}

        {showPublishButton && (
          <div className="col-start-2 flex justify-center">
            <PublishModal
              onConfirm={onPublish}
              canManage={canManage}
              selectedVersion={selectedVersion}
              scheduleReviewPeriod={
                documentVersion.document.scheduleReviewPeriod
              }
              lastStep={lastStep}
            />
          </div>
        )}

        <TimelineEnd
          reviewTo={documentVersion.reviewTo}
          publishedAt={documentVersion.publishedAt}
        />

        {!isDraft && (
          <TimelineIndicator
            icon={Archive}
            text={`${t(($) => $.commons.documentStatus.archived)} ${formatDate(selectedVersion.archivedAt, "date")}`}
            variant={selectedVersion.archivedAt ? "primary" : "secondary"}
          />
        )}
      </div>
    </div>
  );
};
